import { IdtComponent, IdtComponentDefinition } from 'shared/models/idt-components/idt-component.models';

/**
 * Return the parent component (the one that contains the given component in its children).
 */
export function findParentComponent(components: IdtComponent[], componentId: string): IdtComponent | undefined {
  return components.find(
    (component: IdtComponent) => (
      component.children != null && Object.keys(component.children).some((slotKey: string) => {
        const current = component.children[slotKey];
        return current != null && current.indexOf(componentId) !== -1;
      })
    )
  );
}

/**
 * Return the flattened list of ids of all direct children of the given component, in any slot.
 */
export function getChildrenIds(component: IdtComponent): string[] {
  if (component == null) {
    return [];
  }

  const slots = Object.keys(component.children || []);

  return slots.reduce((children, slot) => {
    const current = component.children[slot];
    children.push(...current);
    return children;
  }, [] as string[]);
}

/**
 * Get all the children of the given layout, moved to the default slot of the given definition.
 * And initialize to empty any other slot.
 *
 * @example
 *   const componentDefinitionHeaderFooter = {
 *     numSlots: 3,
 *     defaultSlot: 2
 *   };
 *
 *   const layoutExample1 = {
 *     children: {
 *       1: ['66792794-cb5e-486f-8cfc-36ae188adf34']
 *     }
 *   };
 *
 *   const newChildren = getLayoutChildren(layoutExample1, componentDefinitionHeaderFooter);
 *
 *   // print
 *   {
 *     1: [],
 *     2: ['66792794-cb5e-486f-8cfc-36ae188adf34'],
 *     3: []
 *   };
 */
export function relocateLayoutChildrenByDefinition(
  layout: IdtComponent,
  componentDefinition: IdtComponentDefinition
): Record<string, string[]> {
  const hasSlotsData = componentDefinition.defaultSlot && componentDefinition.numSlots;
  const layoutChildren = hasSlotsData && Array
    .from(Array(componentDefinition.numSlots))
    .reduce((itLayout: Record<string, string[]>, current, slotPosition) => {
      /* eslint-disable */
      slotPosition++;
      if (slotPosition === componentDefinition.defaultSlot && isDefined(layout.children)) {
        itLayout[slotPosition] = Object.keys(layout.children)
          .map((key) => layout.children[key])
          .reduce((acc, value) => acc.concat(value), []);
      } else {
        itLayout[slotPosition] = [];
      }

      /* eslint-enable */
      return itLayout;
    }, {});

  return layoutChildren || layout.children;
}

/**
 * Return the list of children of given parent keeping the order they have in the father.
 * WARNING: changing this function may lead to incorrect rendering when displaying layers
 */
export function getComponentChildren(components: IdtComponent[], parentComponent: IdtComponent): IdtComponent[] {
  if (components.length === 0) {
    return [];
  }

  const childrenComponents: IdtComponent[] = [];
  const sliceChildrenIds = Object.keys(parentComponent.children)
    .reduce((acc, key) => acc.add(parentComponent.children[key].toString()), new Set());
  const componentsFiltered = components.filter((component) => sliceChildrenIds.has(component.id));

  sliceChildrenIds.forEach((children) => {
    componentsFiltered.forEach((component) => {
      if (component.id === children) {
        childrenComponents.push(component);
      }
    });
  });


  return childrenComponents;
}
